import { naturalLog } from '../../../lib/maths'

const SALARY_GROWTH = 'SALARY_GROWTH'

export const fteCostPerDivision = (data, decisions) => {
  const nonSalaryGrowth = data.divisions.filter(d => d.type !== SALARY_GROWTH).map(division => {
    const totalPeopleCosts = data.totalPeopleCosts
    const divisionCostSplit = division.costSplit / 10000
    const startingCost = totalPeopleCosts * divisionCostSplit
    const fteCosts = division.fteCosts
    const fteNewCosts = division.fteNewCosts
    const notionalFTEOutput = (startingCost * 1000000) / (fteCosts * 1000)
    const extraEffort = decisions[division.type] / 100 / 100
    const extraFTERequired = notionalFTEOutput * extraEffort
    const fteAfterExtraEffort = notionalFTEOutput + extraFTERequired
    const fteChange = fteAfterExtraEffort - notionalFTEOutput
    const finalCost = fteChange * (fteNewCosts / 1000) + startingCost
    const changeInCost = finalCost - startingCost
    // console.log({division: division.id, totalPeopleCosts, divisionCostSplit, startingCost, fteCosts, fteNewCosts, fteChange, changeInCost})
    return { division: division.id, startingCost, fteCosts, fteNewCosts, fteChange, finalCost, changeInCost }
  })

  const totalStartingCost = nonSalaryGrowth.reduce((res, d) => res + d.startingCost, 0)
  const totalFinalCost = nonSalaryGrowth.reduce((res, d) => res + d.finalCost, 0) * (1 + (decisions[SALARY_GROWTH] / 100 / 100))
  const totalChangeInCost = nonSalaryGrowth.reduce((res, d) => res + d.changeInCost, 0)
  // console.log({ totalStartingCost, totalFinalCost, totalChangeInCost })
  const salaryGrowthChangeInCost = totalFinalCost - totalStartingCost - totalChangeInCost

  return nonSalaryGrowth.concat({ fteChange: 0, changeInCost: salaryGrowthChangeInCost })
}

const getNpsImpactPerDecision = (id, data, rawEffectiveProductivityDecisions) => {
  const division = data.divisions.find(d => d.id === id)
  const divisionMaxValue = naturalLog(division.maxValue)
  const divisionImpact = rawEffectiveProductivityDecisions[division.type].log / divisionMaxValue * division.customerSatisfaction / 100
  // console.log({ id, divisionMaxValue, divisionImpact, log: rawEffectiveProductivityDecisions[division.type].log })
  return divisionImpact
}

const getCustomerSatisfaction = (data, rawEffectiveProductivityDecisions) => {
  const openingNps = data.npsOpening / 100
  const annualChange = data.npsChange / 100

  const division1Impact = getNpsImpactPerDecision(1, data, rawEffectiveProductivityDecisions)
  const division2Impact = getNpsImpactPerDecision(2, data, rawEffectiveProductivityDecisions)
  const division3Impact = getNpsImpactPerDecision(3, data, rawEffectiveProductivityDecisions)
  const division4Impact = getNpsImpactPerDecision(4, data, rawEffectiveProductivityDecisions)
  const division5Impact = getNpsImpactPerDecision(5, data, rawEffectiveProductivityDecisions)

  const sumOfParts = openingNps + annualChange + division1Impact + division2Impact + division3Impact + division4Impact + division5Impact
  const closingNps = Math.min(sumOfParts, 100)
  const change = closingNps - openingNps
  // console.log({ openingNps, annualChange, division1Impact, division2Impact, division3Impact, division4Impact, division5Impact, sumOfParts, change, closingNps })
  return { change, value: closingNps }
}

const getImpactPerDecisionForField = (id, data, rawEffectiveProductivityDecisions, field) => {
  const division = data.divisions.find(d => d.id === id)
  const divisionMaxValue = naturalLog(division.maxValue)
  const divisionImpact = rawEffectiveProductivityDecisions[division.type].log / divisionMaxValue * division[field]
  // console.log({ id, divisionMaxValue, divisionImpact, log: rawEffectiveProductivityDecisions[division.type].log })
  return divisionImpact
}

const getCustomerComplaints = (data, rawEffectiveProductivityDecisions) => {
  const openingComplaints = data.complaintsOpening
  const annualChange = data.complaintsChange

  const division1Impact = getImpactPerDecisionForField(1, data, rawEffectiveProductivityDecisions, 'complaints')
  const division2Impact = getImpactPerDecisionForField(2, data, rawEffectiveProductivityDecisions, 'complaints')
  const division3Impact = getImpactPerDecisionForField(3, data, rawEffectiveProductivityDecisions, 'complaints')
  const division4Impact = getImpactPerDecisionForField(4, data, rawEffectiveProductivityDecisions, 'complaints')
  const division5Impact = getImpactPerDecisionForField(5, data, rawEffectiveProductivityDecisions, 'complaints')

  const sumOfParts = openingComplaints + annualChange + division1Impact + division2Impact + division3Impact + division4Impact + division5Impact
  const closingComplaints = Math.max(sumOfParts, 0)
  const change = closingComplaints - openingComplaints
  // console.log({ openingComplaints, annualChange, division1Impact, division2Impact, division3Impact, division4Impact, division5Impact, sumOfParts, change, closingComplaints })
  return { change, value: closingComplaints }
}

const getEmployeeEngagementImpactForDivision = (id, data, decisions) => {
  const division = data.divisions.find(d => d.id === id)
  const divisionEmployeeEngagementSetup = division.employeeEngagement
  const divisionDecision = naturalLog(decisions[division.type] / 100)
  const divisionMaxValue = naturalLog(division.maxValue)
  const divisionImpact = divisionDecision / divisionMaxValue * divisionEmployeeEngagementSetup
  // console.log({ id, divisionEmployeeEngagementSetup, divisionDecision, divisionMaxValue, divisionImpact })
  return divisionImpact
}

const getSalaryImpact = (data, decisions) => {
  const salaryDecision = decisions[SALARY_GROWTH]
  let percentage = data.employeeEngagementSalaryIncrease
  if (salaryDecision < 0) {
    percentage = data.employeeEngagementSalaryDecrease
  }
  const decision = naturalLog(salaryDecision / 100)
  const maxValue = naturalLog(data.divisions.find(d => d.type === SALARY_GROWTH).maxValue)
  const impact = decision / maxValue * percentage
  return impact
}

const getEmployeeEngagement = (data, decisions) => {
  const openingEmployeeEngagement = data.employeeEngagementOpening
  const annualChange = data.employeeEngagementChange

  const division3Impact = getEmployeeEngagementImpactForDivision(3, data, decisions)
  const division4Impact = getEmployeeEngagementImpactForDivision(4, data, decisions)
  const salaryImpact = getSalaryImpact(data, decisions)
  const sumOfParts = openingEmployeeEngagement + annualChange + division3Impact + division4Impact + salaryImpact
  const closingEmployeeEngagement = Math.min(sumOfParts, 10000)
  const change = closingEmployeeEngagement - openingEmployeeEngagement
  // console.log({ openingEmployeeEngagement, annualChange, division3Impact, division4Impact, sumOfParts, change, closingEmployeeEngagement })
  return { change, value: closingEmployeeEngagement }
}

const getRiskIncidents = (data, rawEffectiveProductivityDecisions) => {
  const openingRiskIncidents = data.riskIncidentsOpening
  const annualChange = data.riskIncidentsChange

  const division1Impact = getImpactPerDecisionForField(1, data, rawEffectiveProductivityDecisions, 'riskIncidents')
  const division2Impact = getImpactPerDecisionForField(2, data, rawEffectiveProductivityDecisions, 'riskIncidents')
  const division3Impact = getImpactPerDecisionForField(3, data, rawEffectiveProductivityDecisions, 'riskIncidents')
  const division4Impact = getImpactPerDecisionForField(4, data, rawEffectiveProductivityDecisions, 'riskIncidents')
  const division5Impact = getImpactPerDecisionForField(5, data, rawEffectiveProductivityDecisions, 'riskIncidents')

  const sumOfParts = openingRiskIncidents + annualChange + division1Impact + division2Impact + division3Impact + division4Impact + division5Impact
  const closingRiskIncidents = Math.max(sumOfParts, 0)
  const change = closingRiskIncidents - openingRiskIncidents
  // console.log({ openingRiskIncidents, annualChange, division1Impact, division2Impact, division3Impact, division4Impact, division5Impact, sumOfParts, change, closingRiskIncidents })
  return { change, value: closingRiskIncidents }
}

const getCommunityTrust = (data, sanctionsIssued, changeInTrust) => {
  const openingCommunityTrust = data.communityTrustOpening
  const annualChange = data.communityTrustChange

  const sanctionsIssuedWithRegulatorImpact = sanctionsIssued * data.regulatorImpact
  const openingCommunityTrustWithChange = openingCommunityTrust + annualChange
  const sanctionsImpact = -Math.min(sanctionsIssuedWithRegulatorImpact, openingCommunityTrustWithChange)
  const riskIncidentImpact = changeInTrust

  const sumOfParts = openingCommunityTrust + annualChange + sanctionsImpact + riskIncidentImpact
  const closingCommunityTrust = Math.max(Math.min(sumOfParts, 10000), 0)
  const change = closingCommunityTrust - openingCommunityTrust
  // console.log({ openingCommunityTrust, sanctionsIssuedWithRegulatorImpact, openingCommunityTrustWithChange, regulatorImpact: data.regulatorImpact, annualChange, sanctionsImpact, riskIncidentImpact, sumOfParts, change, closingCommunityTrust })
  return { change, value: closingCommunityTrust }
}

const getRegulatorActions = (data, sanctionsIssued) => {
  return { change: sanctionsIssued + data.regulatorActions, value: sanctionsIssued + data.regulatorActions + data.regulatorActionsOpening }
}

export const getImpacts = (data, decisions) => {
  const employeeEngagement = getEmployeeEngagement(data, decisions)
  const engagementGain = (employeeEngagement.value - data.openingRoundEmployeeEngagement) / data.openingRoundEmployeeEngagement
  let outputGainSetup = data.outputGain
  if (engagementGain < 0) {
    outputGainSetup = data.outputLoss
  }
  const outputGain = outputGainSetup * engagementGain
  const outputChange = outputGain

  const rawEffectiveProductivityDecisions = Object.keys(decisions).reduce((acc, d) => {
    if (d === SALARY_GROWTH) {
      acc[d] = {
        value: decisions[d],
        log: naturalLog(decisions[d] / 100)
      }
    } else {
      acc[d] = {
        value: decisions[d] + outputChange,
        log: naturalLog((decisions[d] + outputChange) / 100)
      }
    }
    return acc
  }, {})

  // console.log({ engagementGain, outputGain, outputChange, rawEffectiveProductivityDecisions })
  const nps = getCustomerSatisfaction(data, rawEffectiveProductivityDecisions)
  const complaints = getCustomerComplaints(data, rawEffectiveProductivityDecisions)
  const riskIncidents = getRiskIncidents(data, rawEffectiveProductivityDecisions)

  const commulativeChangeInRiskIncidents = riskIncidents.value - data.openingRoundRiskIncidents

  let changeInTrust = 0
  if (data.communityTrustChangeThreshold) {
    const signage = commulativeChangeInRiskIncidents < 0 ? 1 : -1
    changeInTrust = Math.floor(Math.abs(commulativeChangeInRiskIncidents) / data.communityTrustChangeThreshold) * (data.communityTrustChangeSetup) * signage
  }

  const riskIncidentsRatio = riskIncidents.value / data.openingRoundRiskIncidents
  const sanctionsIssued = Math.max(Math.floor(Math.exp(riskIncidentsRatio) - Math.exp(1)), 0)
  // console.log({ commulativeChangeInRiskIncidents, changeInTrust, riskIncidentsRatio, sanctionsIssued })

  const regulatorActions = getRegulatorActions(data, sanctionsIssued)

  const communityTrust = getCommunityTrust(data, sanctionsIssued, changeInTrust)

  return {
    employeeEngagement,
    nps,
    complaints,
    riskIncidents,
    communityTrust,
    regulatorActions,
    effectiveDecisions: Object.keys(rawEffectiveProductivityDecisions).reduce((acc, d) => {
      acc[d] = rawEffectiveProductivityDecisions[d].value
      return acc
    }, {})
  }
}
